import React, { useMemo, useState } from 'react';
import type {
  Calendar as CalendarType,
  Candidate,
  CandidateWorkFeedItem,
} from '@axiom/validation';
import { useApi, useBreakpoint } from '@axiom/ui';

import {
  InterviewAvailabilityUtil,
  InterviewAvailabilityConst,
} from '../../utils/interview-availability-util';
import type { WorkFeedViewsType } from '../../utils/WorkFeedUtil';
import { WorkFeedUtil } from '../../utils/WorkFeedUtil';
import { InterviewAvailabilityShareable } from '../InterviewAvailabilityShareable/InterviewAvailabilityShareable';
import { WorkFeedConfirmInterestModal } from '../WorkFeedConfirmInterestModal/WorkFeedConfirmInterestModal';
import { WorkFeedConfirmInterestSelfServiceModal } from '../WorkFeedConfirmInterestSelfServiceModal/WorkFeedConfirmInterestSelfServiceModal';
import { WorkFeedQualificationWarningModal } from '../WorkFeedQualificationWarningModal/WorkFeedQualificationWarningModal';
import { WorkFeedSelfServiceModal } from '../WorkFeedSelfServiceModal/WorkFeedSelfServiceModal';
import { CalendarApi } from '../../api/calendar-api';
import { CandidateEngagementsApi } from '../../api/candidate-engagements-api';
import { CandidateExperienceApi } from '../../api/candidate-experience-api';
import { CandidateOpportunitiesLegacyApi } from '../../api/candidate-opportunities-legacy-api';
import { WorkFeedApi } from '../../api/work-feed-api';

const { minimumHoursRequired } = InterviewAvailabilityConst;
const { calculateAvailabilityWithinNextTwoWeeks } = InterviewAvailabilityUtil;

export type ExpressInterestFlowProps = {
  candidate: Candidate;
  onClose: () => void;
  workFeedItem: CandidateWorkFeedItem;
  view: WorkFeedViewsType;
  name?: string;
};

export const ExpressInterestFlow = ({
  candidate,
  onClose,
  workFeedItem,
  view,
  name = 'EXPRESS_INTEREST_FLOW',
}: ExpressInterestFlowProps) => {
  const { isMobile } = useBreakpoint();
  const isSelfService = workFeedItem.opportunity.selfService;

  const { id: calendarId = null } = candidate.calendar || {};

  const [
    { interestedCount },
    { data: candidateOpportunities },
    { data: experiences },
    { data: calendarData } = {
      data: {
        events: null,
      } as Partial<CalendarType>,
    },
  ] = useApi(
    WorkFeedApi.readWorkFeedInterestedCount(candidate.id),
    CandidateOpportunitiesLegacyApi.readOpportunitiesByCandidateId(
      candidate.id
    ),
    CandidateExperienceApi.readExperiences(candidate.id),
    calendarId ? CalendarApi.readCalendar(calendarId) : undefined
  );

  const qualificationWarnings = useMemo(
    () =>
      WorkFeedUtil.getQualificationWarnings(
        candidate,
        workFeedItem,
        candidateOpportunities,
        experiences
      ),
    [candidate, workFeedItem, candidateOpportunities, experiences]
  );

  const [isQualificationWarningModalOpen, setIsQualificationWarningModalOpen] =
    useState(qualificationWarnings.length > 0);
  const [isSelfServiceModalOpen, setIsSelfServiceModalOpen] = useState(
    !isQualificationWarningModalOpen && isSelfService
  );
  const [isExpressInterestModalOpen, setIsExpressInterestModalOpen] = useState(
    !isQualificationWarningModalOpen && !isSelfService
  );
  const [isInterviewModalOpen, setIsInterviewModalOpen] = useState(false);

  const shouldShowInterviewAvailabilityModal =
    calculateAvailabilityWithinNextTwoWeeks(calendarData.events ?? []) <
      minimumHoursRequired && !isMobile;

  const handleClose = async () => {
    await Promise.all([
      WorkFeedApi.refreshAllOfWorkFeed(
        candidate.id,
        WorkFeedUtil.isSinglePageView(view)
          ? workFeedItem.opportunity.id
          : undefined,
        true
      ),
      CandidateEngagementsApi.refreshEngagementsCount(candidate.id),
      CandidateEngagementsApi.refreshEngagementsCurrent(candidate.id),
      CandidateEngagementsApi.refreshEngagementsHistory(candidate.id),
    ]);

    WorkFeedUtil.addInterestedToasts(
      interestedCount,
      `../../${workFeedItem.opportunity.id}`
    );

    onClose();
  };

  return (
    <div data-test={name}>
      {isQualificationWarningModalOpen ? (
        <WorkFeedQualificationWarningModal
          name="WORK_FEED_QUALIFICATION_WARNING_MODAL"
          candidate={candidate}
          onAfterSubmit={() => {
            setIsQualificationWarningModalOpen(false);
            if (isSelfService) {
              setIsSelfServiceModalOpen(true);
            } else {
              setIsExpressInterestModalOpen(true);
            }
          }}
          onClose={() => {
            onClose();
          }}
          workFeedItem={workFeedItem}
        />
      ) : null}
      {isSelfServiceModalOpen ? (
        <WorkFeedSelfServiceModal
          name="WORK_FEED_SELF_SERVICE_MODAL"
          candidate={candidate}
          onAfterSubmit={() => {
            setIsSelfServiceModalOpen(false);
            setIsExpressInterestModalOpen(true);
          }}
          onClose={() => {
            onClose();
          }}
          workFeedItem={workFeedItem}
        />
      ) : null}
      {isExpressInterestModalOpen ? (
        isSelfService ? (
          <WorkFeedConfirmInterestSelfServiceModal
            name="CONFIRM_INTEREST_SELF_SERVICE_MODAL"
            candidate={candidate}
            onAfterSubmit={async () => {
              if (shouldShowInterviewAvailabilityModal) {
                setIsExpressInterestModalOpen(false);
                setIsInterviewModalOpen(true);
              } else {
                await handleClose();
              }
            }}
            onClose={() => {
              onClose();
            }}
            workFeedItem={workFeedItem}
            submitButtonText={
              shouldShowInterviewAvailabilityModal ? 'Next' : 'Submit'
            }
          />
        ) : (
          <WorkFeedConfirmInterestModal
            name="CONFIRM_INTEREST_MODAL"
            candidate={candidate}
            onAfterSubmit={async () => {
              if (shouldShowInterviewAvailabilityModal) {
                setIsExpressInterestModalOpen(false);
                setIsInterviewModalOpen(true);
              } else {
                await handleClose();
              }
            }}
            onClose={() => {
              onClose();
            }}
            workFeedItem={workFeedItem}
            submitButtonText={
              shouldShowInterviewAvailabilityModal ? 'Next' : 'Submit'
            }
          />
        )
      ) : null}
      {isInterviewModalOpen ? (
        <InterviewAvailabilityShareable
          name="INTERVIEW_AVAILABILITY_SHAREABLE"
          candidateId={candidate.id}
          message="Axiom has received your expression of interest. You can now track this opportunity in your Engagements page."
          title={
            isSelfService
              ? `Submit profile to ${workFeedItem.opportunity.accountName ?? 'Client'}`
              : 'Submit profile to Axiom'
          }
          onAfterSubmit={async () => handleClose()}
          onClose={async () => handleClose()}
          showSelfService={isSelfService}
          submitButtonText="Submit"
        />
      ) : null}
    </div>
  );
};
